import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AdminAccountController } from '../../../../@core/api/controllers/admin-account/admin-account.controller';
import { SuperAdminAccountController } from '../../../../@core/api/controllers/super-admin-account/super-admin-account.controller';
import { AccountService } from '../../../../@core/services/account.service';
import { TokenService } from '../../../../@core/services/token.service';
import { URLS } from '../../../../shared/enums/urls.enum';
import { AuthMapper } from '../api/auth.mapper';
import { IForgotEmailPayload } from '../interfaces/forgot-email.interface';
import { ILogin, ILoginPayload, ILoginResponseDTO } from '../interfaces/login.interface';
import { IRegisterPayload } from '../interfaces/register.interface';
import { IResetPasswordPayload } from '../interfaces/reset-password.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private tokenService: TokenService,
    private accountService: AccountService,
    private accountController: AdminAccountController,
    private superAdminAccountController: SuperAdminAccountController,
    private router: Router,
  ) {}

  login(payload: ILoginPayload): Promise<ILogin> {
    return new Promise<ILogin>((resolve) => {
      this.superAdminAccountController.login(payload).subscribe({
        next: (response: ILoginResponseDTO) => {
          const loginData = AuthMapper.fromDTO(response);
          const { accessToken, refreshToken } = loginData;
          this.tokenService.setCurrentAccessToken(accessToken);
          this.tokenService.setCurrentRefreshToken(refreshToken);

          resolve(loginData);
        },
        error: (error) => {
          console.error('Login error:', error);
          resolve(null);
        },
      });
    });
  }

  register(payload: IRegisterPayload): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.accountController.register(payload).subscribe({
        next: () => {
          resolve(true);
        },
        error: (error) => {
          console.error('Register error:', error);
          resolve(false);
        },
      });
    });
  }

  logout(): Promise<boolean> {
    this.accountService.clearUser();
    this.router.navigate([URLS.LOGIN_PATH]);

    const logoutPromise = new Promise<boolean>((resolve) => {
      this.accountController.logout().subscribe({
        next: () => {
          resolve(true);
        },
        error: (error) => {
          console.error('Logout error:', error);
          resolve(false);
        },
      });
    });

    this.tokenService.clearTokens();

    return logoutPromise;
  }

  forgotPassword(email: IForgotEmailPayload): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.accountController.forgotPassword(email).subscribe({
        next: () => {
          resolve(true);
        },
        error: (error) => {
          console.error('Forgot password error:', error);
          resolve(false);
        },
      });
    });
  }

  resetPassword(payload: IResetPasswordPayload): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.accountController.resetPassword(payload).subscribe({
        next: () => {
          resolve(true);
        },
        error: (error) => {
          console.error('Reset password error:', error);
          resolve(false);
        },
      });
    });
  }

  clearAuthData(): void {
    this.tokenService.clearTokens();
    this.accountService.clearUser();
  }

  isAuthenticated(): boolean {
    const token = TokenService.getCurrentAccessToken();
    return !!token;
  }
}
